import { OrganisationWithRole } from '@fsp/types';
import { isTrialExpired } from '@fsp/shared';
import {
  AssignmentLateTwoTone,
  AssignmentOutlined,
  BusinessTwoTone,
  ChevronLeft,
  ChevronRight,
  DashboardTwoTone,
  EditNote,
  FactCheckTwoTone,
  InventoryTwoTone,
  MenuBookTwoTone,
  SettingsTwoTone,
} from '@mui/icons-material';
import {
  AppBar,
  Badge,
  Box,
  Chip,
  CssBaseline,
  Divider,
  Drawer,
  IconButton,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Stack,
  Toolbar,
  Tooltip,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import React, { useEffect, useState } from 'react';
import { Navigate, Outlet, useLocation, useParams } from 'react-router-dom';
import { usePreserveParamsNavigation } from '../hooks/usePreserveParamsNavigation';
import ChapterIcon from '../components/ChapterIcon';
import { FreeTrialBanner } from '../components/FreeTrialBanner';
import { Logo } from '../components/Logo';
import { PageGenerationStatus } from '../components/PageGenerationStatus';
import { ProfileMenu } from '../components/ProfileMenu';
import UserAvatar from '../components/UserAvatar';
import { useAuth } from '../contexts/AuthContext';
import { useNotifications } from '../contexts/NotificationsContext';
import { usePlan } from '../contexts/PlanContext';
import { useReleases } from '../contexts/ReleasesContext';

const drawerWidth = 240;
const drawerWidthCollapsed = 64;

export const AppWrapper: React.FC = () => {
  const navigate = usePreserveParamsNavigation();
  const location = useLocation();
  const { user, currentOrganisation } = useAuth();
  const { notificationCount } = useNotifications();
  const { currentPlan, chapters } = usePlan();
  const { viewMode, currentRelease, currentReleaseChapters, latestRelease } = useReleases();
  const { releaseId } = useParams<{ releaseId?: string }>();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [sidebarOpen, setSidebarOpen] = useState(true);

  // Collapse drawer by default on mobile
  useEffect(() => {
    if (isMobile) {
      setSidebarOpen(false);
    }
  }, [isMobile]);

  // Redirect expired trial users to upgrade page
  if (currentOrganisation && isTrialExpired(currentOrganisation)) {
    return <Navigate to="/upgrade" replace />;
  }

  const navItems = [
    { text: 'Dashboard', path: '/app/', icon: DashboardTwoTone, dataNavItem: 'dashboard' },
    { text: 'Plans', path: '/app/plans', icon: MenuBookTwoTone, dataNavItem: 'plans' },
    { text: 'Suppliers', path: '/app/suppliers', icon: BusinessTwoTone, dataNavItem: 'suppliers' },
    { text: 'Settings', path: '/settings', icon: SettingsTwoTone, dataNavItem: 'settings' },
  ];

  const handleMenu = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const toggleSidebar = () => {
    setSidebarOpen(!sidebarOpen);
  };

  const isActive = (path: string) => {
    if (path === '/app/' && location.pathname === '/app/') return true;
    return path !== '/app/' && location.pathname.startsWith(path);
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100vh' }}>
      <CssBaseline />
      <FreeTrialBanner />
      <AppBar
        position="static"
        sx={{
          borderLeft: 0,
        }}
      >
        <Toolbar sx={{ mr: 6, ml: 3, borderBottom: '0.5px solid #000000' }}>
          <Logo />
          {currentPlan && (
            <>
              <Typography
                variant="body1"
                noWrap
                sx={{
                  mr: 3,
                  fontWeight: 600,
                  color: 'text.primary',
                }}
              >
                {currentPlan.name}
              </Typography>
            </>
          )}
          <Box sx={{ flexGrow: 1 }} />
          <Stack direction="row" alignItems="center" spacing={2}>
            <OrganisationHeader currentOrganisation={currentOrganisation} />

            <IconButton size="large" onClick={handleMenu} color="inherit">
              <Badge
                badgeContent={notificationCount}
                color="error"
                invisible={notificationCount === 0}
                sx={{
                  '& .MuiBadge-badge': {
                    fontSize: '0.75rem',
                    height: '18px',
                    minWidth: '18px',
                  },
                }}
              >
                <UserAvatar
                  initials={
                    (user?.firstName?.[0] || '')?.toUpperCase() +
                    (user?.lastName?.[0] || '')?.toUpperCase()
                  }
                  avatarUrl={user?.avatarUrl || undefined}
                  size="small"
                  color="primary.main"
                />
              </Badge>
            </IconButton>
          </Stack>
          <ProfileMenu
            anchorEl={anchorEl}
            open={Boolean(anchorEl)}
            onClose={handleClose}
            user={user}
            userRole={currentOrganisation?.role as any}
          />
        </Toolbar>
      </AppBar>
      <Box sx={{ display: 'flex', flex: 1, overflow: 'hidden' }}>
        <Drawer
          sx={{
            width: sidebarOpen ? drawerWidth : drawerWidthCollapsed,
            flexShrink: 0,
            '& .MuiDrawer-paper': {
              width: sidebarOpen ? drawerWidth : drawerWidthCollapsed,
              boxSizing: 'border-box',
              position: 'relative',
              height: '100%',
              transition: 'width 0.3s',
              overflowX: 'hidden',
              display: 'flex',
              flexDirection: 'column',
            },
          }}
          variant="permanent"
          anchor="left"
        >
          <Box sx={{ mt: 1.5, ml: 1, flex: 1, display: 'flex', flexDirection: 'column' }}>
            <List sx={{ flex: 1, py: 0 }}>
              {navItems.map((item) => {
                const Icon = item.icon;
                const active = isActive(item.path);

                return (
                  <Tooltip key={item.text} title={!sidebarOpen ? item.text : ''} placement="right">
                    <ListItemButton
                      selected={active}
                      onClick={() => navigate(item.path)}
                      data-nav-item={item.dataNavItem}
                      sx={{
                        minHeight: 32,
                        height: 32,
                        px: 0,
                        ml: 1.5,
                        mr: 0,
                        mb: 0.25,
                      }}
                    >
                      <ListItemIcon
                        sx={{
                          minWidth: 36,
                          justifyContent: 'center',
                          mr: sidebarOpen ? 1.5 : 0.5,
                        }}
                      >
                        <Icon sx={{ fontSize: 20 }} />
                      </ListItemIcon>
                      {sidebarOpen && (
                        <ListItemText
                          primary={item.text}
                          primaryTypographyProps={{
                            fontSize: '0.875rem',
                          }}
                          sx={{
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                            m: 0,
                          }}
                        />
                      )}
                    </ListItemButton>
                  </Tooltip>
                );
              })}

              {/* Chapters section - context aware based on view mode */}
              {currentPlan && (
                <>
                  {viewMode === 'edit' && chapters.length > 0 && (
                    <>
                      <Divider sx={{ my: 0.75, mx: 1 }} />
                      {sidebarOpen && (
                        <Typography
                          variant="caption"
                          sx={{
                            px: 2.5,
                            py: 0.5,
                            fontWeight: 600,
                            color: 'success.main',
                            textTransform: 'uppercase',
                            letterSpacing: 0.5,
                            fontSize: '0.65rem',

                            whiteSpace: 'nowrap',
                            overflow: 'hidden',
                          }}
                        >
                          Editing new version
                        </Typography>
                      )}
                      {chapters
                        .sort((a, b) => a.sortOrder - b.sortOrder)
                        .map((chapter) => {
                          const chapterPath = `/app/plans/${currentPlan.id}/new/chapters/${chapter.id}/pages`;
                          const chapterBasePath = `/app/plans/${currentPlan.id}/new/chapters/${chapter.id}`;
                          const chapterActive = isActive(chapterBasePath);

                          return (
                            <Tooltip
                              key={chapter.id}
                              title={!sidebarOpen ? chapter.title : ''}
                              placement="right"
                            >
                              <ListItemButton
                                selected={chapterActive}
                                onClick={() => navigate(chapterPath)}
                                data-onboarding={`chapter-${chapter.type}`}
                                sx={{
                                  minHeight: 32,
                                  height: 32,
                                  px: 0,
                                  ml: 1.5,
                                  mr: 0,
                                  mb: 0.25,
                                }}
                              >
                                <ListItemIcon
                                  sx={{
                                    minWidth: 36,
                                    justifyContent: 'center',
                                    mr: sidebarOpen ? 1.5 : 0.5,
                                  }}
                                >
                                  <ChapterIcon
                                    type={chapter.type}
                                    sx={{ fontSize: 18, color: 'primary.light' }}
                                  />
                                </ListItemIcon>
                                {sidebarOpen && (
                                  <ListItemText
                                    primary={chapter.title}
                                    primaryTypographyProps={{
                                      fontSize: '0.875rem',
                                    }}
                                    sx={{
                                      overflow: 'hidden',
                                      textOverflow: 'ellipsis',
                                      whiteSpace: 'nowrap',
                                      m: 0,
                                    }}
                                  />
                                )}
                              </ListItemButton>
                            </Tooltip>
                          );
                        })}
                    </>
                  )}
                  {viewMode === 'latest' &&
                    releaseId &&
                    currentRelease &&
                    currentReleaseChapters.length > 0 && (
                      <>
                        <Divider sx={{ my: 0.75, mx: 1 }} />
                        {sidebarOpen && (
                          <Box
                            sx={{
                              px: 2.5,
                              py: 0.5,
                              display: 'flex',
                              alignItems: 'center',
                              gap: 0.5,
                            }}
                          >
                            <Typography
                              variant="caption"
                              sx={{
                                fontWeight: 600,
                                color: 'primary.main',
                                textTransform: 'uppercase',
                                letterSpacing: 0.5,
                                fontSize: '0.65rem',
                              }}
                            >
                              Version {currentRelease.versionLabel}
                            </Typography>
                            {latestRelease && currentRelease.id === latestRelease.id && (
                              <Chip
                                label="Latest"
                                size="small"
                                color="primary"
                                sx={{
                                  height: 16,
                                  fontSize: '0.6rem',
                                  fontWeight: 600,
                                  '& .MuiChip-label': {
                                    px: 0.75,
                                    py: 0,
                                  },
                                }}
                              />
                            )}
                          </Box>
                        )}
                        {currentReleaseChapters
                          .sort((a, b) => a.sortOrderAtRelease - b.sortOrderAtRelease)
                          .map((chapterSnapshot) => {
                            const chapterPath = `/app/plans/${currentPlan.id}/releases/${currentRelease.id}/chapters/${chapterSnapshot.chapterId}`;
                            const chapterBasePath = `/app/plans/${currentPlan.id}/releases/${currentRelease.id}/chapters/${chapterSnapshot.chapterId}`;
                            const chapterActive = isActive(chapterBasePath);

                            return (
                              <Tooltip
                                key={chapterSnapshot.chapterId}
                                title={!sidebarOpen ? chapterSnapshot.titleAtRelease : ''}
                                placement="right"
                              >
                                <ListItemButton
                                  selected={chapterActive}
                                  onClick={() => navigate(chapterPath)}
                                  sx={{
                                    minHeight: 32,
                                    height: 32,
                                    px: 0,
                                    ml: 1.5,
                                    mr: 0,
                                    mb: 0.25,
                                  }}
                                >
                                  <ListItemIcon
                                    sx={{
                                      minWidth: 36,
                                      justifyContent: 'center',
                                      mr: sidebarOpen ? 1.5 : 0.5,
                                    }}
                                  >
                                    <ChapterIcon
                                      type={chapterSnapshot.typeAtRelease}
                                      sx={{ fontSize: 18, color: 'primary.light' }}
                                    />
                                  </ListItemIcon>
                                  {sidebarOpen && (
                                    <ListItemText
                                      primary={chapterSnapshot.titleAtRelease}
                                      primaryTypographyProps={{
                                        fontSize: '0.875rem',
                                      }}
                                      sx={{
                                        overflow: 'hidden',
                                        textOverflow: 'ellipsis',
                                        whiteSpace: 'nowrap',
                                        m: 0,
                                      }}
                                    />
                                  )}
                                </ListItemButton>
                              </Tooltip>
                            );
                          })}
                      </>
                    )}

                  {/* Records section - show in edit mode */}
                  {viewMode === 'edit' && (
                    <>
                      <Divider sx={{ my: 0.75, mx: 1 }} />
                      {sidebarOpen && (
                        <Typography
                          variant="caption"
                          sx={{
                            px: 2.5,
                            py: 0.5,
                            fontWeight: 600,
                            color: 'text.secondary',
                            textTransform: 'uppercase',
                            letterSpacing: 0.5,
                            fontSize: '0.65rem',

                            whiteSpace: 'nowrap',
                            overflow: 'hidden',
                          }}
                        >
                          Records and Audits
                        </Typography>
                      )}
                      <Tooltip title={!sidebarOpen ? 'Record Collections' : ''} placement="right">
                        <ListItemButton
                          selected={isActive(`/app/plans/${currentPlan.id}/records`)}
                          onClick={() => navigate(`/app/plans/${currentPlan.id}/records`)}
                          data-onboarding="nav-record-collections"
                          sx={{
                            minHeight: 32,
                            height: 32,
                            px: 0,
                            ml: 1.5,
                            mr: 0,
                            mb: 0.25,
                          }}
                        >
                          <ListItemIcon
                            sx={{
                              minWidth: 36,
                              justifyContent: 'center',
                              mr: sidebarOpen ? 1.5 : 0.5,
                            }}
                          >
                            <AssignmentOutlined sx={{ fontSize: 18, color: 'primary.light' }} />
                          </ListItemIcon>
                          {sidebarOpen && (
                            <ListItemText
                              primary="Record Collections"
                              primaryTypographyProps={{
                                fontSize: '0.875rem',
                              }}
                              sx={{
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap',
                                m: 0,
                              }}
                            />
                          )}
                        </ListItemButton>
                      </Tooltip>
                      <Tooltip title={!sidebarOpen ? 'Audits' : ''} placement="right">
                        <ListItemButton
                          selected={isActive(`/app/plans/${currentPlan.id}/audits`)}
                          onClick={() => navigate(`/app/plans/${currentPlan.id}/audits`)}
                          sx={{
                            minHeight: 32,
                            height: 32,
                            px: 0,
                            ml: 1.5,
                            mr: 0,
                            mb: 0.25,
                          }}
                        >
                          <ListItemIcon
                            sx={{
                              minWidth: 36,
                              justifyContent: 'center',
                              mr: sidebarOpen ? 1.5 : 0.5,
                            }}
                          >
                            <FactCheckTwoTone sx={{ fontSize: 18, color: 'primary.light' }} />
                          </ListItemIcon>
                          {sidebarOpen && (
                            <ListItemText
                              primary="Audits"
                              primaryTypographyProps={{
                                fontSize: '0.875rem',
                              }}
                              sx={{
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap',
                                m: 0,
                              }}
                            />
                          )}
                        </ListItemButton>
                      </Tooltip>
                      <Tooltip title={!sidebarOpen ? 'Incoming Goods' : ''} placement="right">
                        <ListItemButton
                          selected={isActive(`/app/plans/${currentPlan.id}/goods`)}
                          onClick={() => navigate(`/app/plans/${currentPlan.id}/goods`)}
                          data-onboarding="nav-incoming-goods"
                          sx={{
                            minHeight: 32,
                            height: 32,
                            px: 0,
                            ml: 1.5,
                            mr: 0,
                            mb: 0.25,
                          }}
                        >
                          <ListItemIcon
                            sx={{
                              minWidth: 36,
                              justifyContent: 'center',
                              mr: sidebarOpen ? 1.5 : 0.5,
                            }}
                          >
                            <InventoryTwoTone sx={{ fontSize: 18, color: 'primary.light' }} />
                          </ListItemIcon>
                          {sidebarOpen && (
                            <ListItemText
                              primary="Incoming Goods"
                              primaryTypographyProps={{
                                fontSize: '0.875rem',
                              }}
                              sx={{
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap',
                                m: 0,
                              }}
                            />
                          )}
                        </ListItemButton>
                      </Tooltip>
                      <Tooltip title={!sidebarOpen ? 'Corrective Actions' : ''} placement="right">
                        <ListItemButton
                          selected={isActive(`/app/plans/${currentPlan.id}/corrective-actions`)}
                          onClick={() =>
                            navigate(`/app/plans/${currentPlan.id}/corrective-actions`)
                          }
                          sx={{
                            minHeight: 32,
                            height: 32,
                            px: 0,
                            ml: 1.5,
                            mr: 0,
                            mb: 0.25,
                          }}
                        >
                          <ListItemIcon
                            sx={{
                              minWidth: 36,
                              justifyContent: 'center',
                              mr: sidebarOpen ? 1.5 : 0.5,
                            }}
                          >
                            <EditNote sx={{ fontSize: 18, color: 'primary.light' }} />
                          </ListItemIcon>
                          {sidebarOpen && (
                            <ListItemText
                              primary="Corrective Actions"
                              primaryTypographyProps={{
                                fontSize: '0.875rem',
                              }}
                              sx={{
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap',
                                m: 0,
                              }}
                            />
                          )}
                        </ListItemButton>
                      </Tooltip>
                    </>
                  )}

                  {/* Records section - show in release view mode */}
                  {viewMode === 'latest' && releaseId && currentRelease && (
                    <>
                      <Divider sx={{ my: 0.75, mx: 1 }} />
                      <Typography
                        variant="caption"
                        sx={{
                          px: 2.5,
                          py: 0.5,
                          fontWeight: 600,
                          color: 'text.secondary',
                          textTransform: 'uppercase',
                          letterSpacing: 0.5,
                          fontSize: '0.65rem',
                          whiteSpace: 'nowrap',
                          overflow: 'hidden',
                        }}
                      >
                        Records and Audits
                      </Typography>

                      <Tooltip title={!sidebarOpen ? 'Record Collections' : ''} placement="right">
                        <ListItemButton
                          selected={isActive(
                            `/app/plans/${currentPlan.id}/releases/${releaseId}/records`
                          )}
                          onClick={() =>
                            navigate(`/app/plans/${currentPlan.id}/releases/${releaseId}/records`)
                          }
                          sx={{
                            minHeight: 32,
                            height: 32,
                            px: 0,
                            ml: 1.5,
                            mr: 0,
                            mb: 0.25,
                          }}
                        >
                          <ListItemIcon
                            sx={{
                              minWidth: 36,
                              justifyContent: 'center',
                              mr: sidebarOpen ? 1.5 : 0.5,
                            }}
                          >
                            <AssignmentOutlined sx={{ fontSize: 18, color: 'primary.light' }} />
                          </ListItemIcon>
                          {sidebarOpen && (
                            <ListItemText
                              primary="Record Collections"
                              primaryTypographyProps={{
                                fontSize: '0.875rem',
                              }}
                              sx={{
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap',
                                m: 0,
                              }}
                            />
                          )}
                        </ListItemButton>
                      </Tooltip>
                      <Tooltip title={!sidebarOpen ? 'Audits' : ''} placement="right">
                        <ListItemButton
                          selected={isActive(`/app/plans/${currentPlan.id}/audits`)}
                          onClick={() => navigate(`/app/plans/${currentPlan.id}/audits`)}
                          sx={{
                            minHeight: 32,
                            height: 32,
                            px: 0,
                            ml: 1.5,
                            mr: 0,
                            mb: 0.25,
                          }}
                        >
                          <ListItemIcon
                            sx={{
                              minWidth: 36,
                              justifyContent: 'center',
                              mr: sidebarOpen ? 1.5 : 0.5,
                            }}
                          >
                            <FactCheckTwoTone sx={{ fontSize: 18, color: 'primary.light' }} />
                          </ListItemIcon>
                          {sidebarOpen && (
                            <ListItemText
                              primary="Audits"
                              primaryTypographyProps={{
                                fontSize: '0.875rem',
                              }}
                              sx={{
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap',
                                m: 0,
                              }}
                            />
                          )}
                        </ListItemButton>
                      </Tooltip>
                      <Tooltip title={!sidebarOpen ? 'Incoming Goods' : ''} placement="right">
                        <ListItemButton
                          selected={isActive(
                            `/app/plans/${currentPlan.id}/releases/${releaseId}/goods`
                          )}
                          onClick={() =>
                            navigate(`/app/plans/${currentPlan.id}/releases/${releaseId}/goods`)
                          }
                          sx={{
                            minHeight: 32,
                            height: 32,
                            px: 0,
                            ml: 1.5,
                            mr: 0,
                            mb: 0.25,
                          }}
                        >
                          <ListItemIcon
                            sx={{
                              minWidth: 36,
                              justifyContent: 'center',
                              mr: sidebarOpen ? 1.5 : 0.5,
                            }}
                          >
                            <InventoryTwoTone sx={{ fontSize: 18, color: 'primary.light' }} />
                          </ListItemIcon>
                          {sidebarOpen && (
                            <ListItemText
                              primary="Incoming Goods"
                              primaryTypographyProps={{
                                fontSize: '0.875rem',
                              }}
                              sx={{
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap',
                                m: 0,
                              }}
                            />
                          )}
                        </ListItemButton>
                      </Tooltip>
                      <Tooltip title={!sidebarOpen ? 'Corrective Actions' : ''} placement="right">
                        <ListItemButton
                          selected={isActive(`/app/plans/${currentPlan.id}/corrective-actions`)}
                          onClick={() =>
                            navigate(`/app/plans/${currentPlan.id}/corrective-actions`)
                          }
                          sx={{
                            minHeight: 32,
                            height: 32,
                            px: 0,
                            ml: 1.5,
                            mr: 0,
                            mb: 0.25,
                          }}
                        >
                          <ListItemIcon
                            sx={{
                              minWidth: 36,
                              justifyContent: 'center',
                              mr: sidebarOpen ? 1.5 : 0.5,
                            }}
                          >
                            <AssignmentLateTwoTone sx={{ fontSize: 18, color: 'primary.light' }} />
                          </ListItemIcon>
                          {sidebarOpen && (
                            <ListItemText
                              primary="Corrective Actions"
                              primaryTypographyProps={{
                                fontSize: '0.875rem',
                              }}
                              sx={{
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap',
                                m: 0,
                              }}
                            />
                          )}
                        </ListItemButton>
                      </Tooltip>
                    </>
                  )}
                </>
              )}
            </List>

            {/* Status indicators at bottom */}
            <Box sx={{ mt: 'auto' }}>
              <Divider sx={{ mb: 1 }} />
              <PageGenerationStatus sidebarOpen={sidebarOpen} />
            </Box>

            <Box
              sx={{
                display: 'flex',
                justifyContent: sidebarOpen ? 'flex-end' : 'center',
                mb: 2,
                ml: sidebarOpen ? 0 : 2,
              }}
            >
              <IconButton onClick={toggleSidebar} size="small">
                {sidebarOpen ? <ChevronLeft /> : <ChevronRight />}
              </IconButton>
            </Box>
          </Box>
        </Drawer>
        <Box
          component="main"
          sx={{
            flexGrow: 1,
            bgcolor: 'background.default',
            p: 3,
            pr: 10,
            overflow: 'auto',
          }}
        >
          <Outlet />
        </Box>
      </Box>
    </Box>
  );
};

const OrganisationHeader: React.FC<{ currentOrganisation: OrganisationWithRole | null }> = ({
  currentOrganisation,
}) => {
  return (
    <Typography variant="body2" color="text.primary" sx={{ fontWeight: 600 }}>
      {currentOrganisation?.name || 'No Organisation'}
      <Typography component="span" variant="caption" sx={{ ml: 1, opacity: 0.7 }}>
        {currentOrganisation?.role}
      </Typography>
    </Typography>
  );
};

export default AppWrapper;
